import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../legalIdVerification/LegalIdVerification.module.scss";

interface ResendLinkProps {
  /** Seconds the user must wait before the resend button appears. Default 30. */
  cooldownSeconds?: number;
  /**
   * Performs the actual resend (the WE `_input` call). The cooldown restarts
   * only if this resolves; if it rejects, the button stays visible so the user
   * can try again.
   */
  onResend: () => void | Promise<void>;
}

/**
 * Rate-limit-aware "Resend Link" control for the phone "Check your phone" screen.
 *
 * <p>While the cooldown runs it shows a live countdown ("You can try again in
 * X seconds") and no button; when it reaches zero the resend button appears.
 * Clicking fires {@link ResendLinkProps.onResend} and, on success, restarts the
 * cooldown — mirroring phones-api's one-resend-per-30-seconds rate limit so the
 * user can't out-click it.
 */
export function ResendLink({ cooldownSeconds = 30, onResend }: ResendLinkProps) {
  const { t } = useTranslation();
  const [secondsLeft, setSecondsLeft] = useState(cooldownSeconds);
  // Bumped after a successful resend to restart the countdown effect.
  const [cycle, setCycle] = useState(0);
  const [busy, setBusy] = useState(false);

  // One interval per cooldown cycle; ticks down to zero, then clears itself.
  useEffect(() => {
    setSecondsLeft(cooldownSeconds);
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(id);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [cycle, cooldownSeconds]);

  const handleClick = useCallback(async () => {
    if (secondsLeft > 0 || busy) return;
    setBusy(true);
    try {
      await onResend();
      setCycle((c) => c + 1); // success → restart the cooldown
    } catch {
      // Leave the button visible so the user can try again. A failed resend
      // must not fail the verification — the original link is still valid.
    } finally {
      setBusy(false);
    }
  }, [secondsLeft, busy, onResend]);

  if (secondsLeft > 0) {
    return (
      <p className={styles.subtitle}>
        {t("phone.verifying.resendCountdown", { seconds: secondsLeft })}
      </p>
    );
  }

  return (
    <button
      type="button"
      className={styles.confirmButton}
      onClick={handleClick}
      disabled={busy}
    >
      {t("phone.verifying.resendButton")}
    </button>
  );
}